import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useBranch } from "../context/BranchContext";
import { PageHeader, StatCard, rupee } from "../components/shared";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Gift, Package, IndianRupee, TrendingDown, ChevronLeft } from "lucide-react";
import { ReportDateFilter, useReportRange } from "../components/ReportDateFilter";


export default function FreedomPackageReport() {
  const navigate = useNavigate();
  const { branchParam } = useBranch();
  const rr = useReportRange("month");
  const { start: from, end: to } = rr;
  const [data, setData] = useState({ rows: [], totals: { packages: 0, sales: 0, savings: 0 } });
  const [detail, setDetail] = useState([]);

  const load = useCallback(() => {
    const params = { from_date: from, to_date: to, branch_id: branchParam };
    api.get("/reports/freedom-package", { params }).then((r) => setData(r.data)).catch(() => setData({ rows: [], totals: { packages: 0, sales: 0, savings: 0 } }));
    api.get("/reports/freedom-package/detail", { params }).then((r) => setDetail(r.data.rows || [])).catch(() => setDetail([]));
  }, [from, to, branchParam]);

  const exportFile = async (fmt) => {
    try {
      const res = await api.get("/reports/freedom-package/export", { params: { from_date: from, to_date: to, branch_id: branchParam, fmt }, responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([res.data], { type: fmt === "pdf" ? "application/pdf" : "text/csv" }));
      link.download = `freedom_report_${from}_${to}.${fmt}`; link.click(); URL.revokeObjectURL(link.href);
    } catch { /* noop */ }
  };

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="freedom-report-page">
      <Button variant="ghost" className="mb-2 text-muted-foreground" onClick={() => navigate("/reports")} data-testid="freedom-report-back"><ChevronLeft size={16} className="mr-1" /> Reports</Button>
      <PageHeader title="Freedom Package Report" subtitle="Branch-wise package sales and customer savings"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => exportFile("csv")} data-testid="freedom-export-csv">CSV</Button>
            <Button variant="outline" className="rounded-sm" onClick={() => exportFile("pdf")} data-testid="freedom-export-pdf">PDF</Button>
          </div>
        } />
      <Card className="p-4 bg-card border-border mb-6"><ReportDateFilter r={rr} prefix="freedom-report" /></Card>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard icon={Package} label="Packages Sold" value={data.totals.packages} />
        <StatCard icon={IndianRupee} label="Total Package Sales" value={rupee(data.totals.sales)} />
        <StatCard icon={TrendingDown} label="Total Customer Savings" value={rupee(data.totals.savings)} />
      </div>

      <Card className="bg-card border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-border">
              <TableHead>Branch</TableHead>
              <TableHead className="text-right">Packages Sold</TableHead>
              <TableHead className="text-right">Actual Value</TableHead>
              <TableHead className="text-right">Package Sales</TableHead>
              <TableHead className="text-right">Customer Savings</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.rows.map((r) => (
              <TableRow key={r.branch_id} className="border-border" data-testid={`freedom-report-row-${r.branch_id}`}>
                <TableCell className="flex items-center gap-2"><Gift size={14} className="text-primary" /> {r.branch_name}</TableCell>
                <TableCell className="text-right">{r.packages}</TableCell>
                <TableCell className="text-right text-muted-foreground line-through">{rupee(r.actual)}</TableCell>
                <TableCell className="text-right text-primary font-medium">{rupee(r.sales)}</TableCell>
                <TableCell className="text-right text-green-400">{rupee(r.savings)}</TableCell>
              </TableRow>
            ))}
            {!data.rows.length && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">No Freedom Package sales in this period.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <h2 className="font-display text-xl mt-8 mb-3">Package Details</h2>
      <Card className="bg-card border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-border">
              <TableHead>Date</TableHead>
              <TableHead>Invoice</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Package · Services (Staff)</TableHead>
              <TableHead className="text-right">Actual</TableHead>
              <TableHead className="text-right">Paid</TableHead>
              <TableHead className="text-right">Saved</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {detail.map((r, i) => (
              <TableRow key={i} className="border-border align-top" data-testid={`freedom-detail-row-${i}`}>
                <TableCell className="whitespace-nowrap">{r.date}</TableCell>
                <TableCell>{r.invoice_no}</TableCell>
                <TableCell>{r.customer_name}<span className="block text-xs text-muted-foreground">{r.customer_mobile}</span></TableCell>
                <TableCell>
                  <span className="text-primary font-medium">Any {r.slab_count}</span>
                  <ul className="text-xs text-muted-foreground mt-1 space-y-0.5">
                    {r.services.map((s, j) => <li key={j}>{s.name} — <span className="text-foreground">{s.staff_name || "—"}</span></li>)}
                  </ul>
                </TableCell>
                <TableCell className="text-right text-muted-foreground line-through">{rupee(r.actual)}</TableCell>
                <TableCell className="text-right text-primary font-medium">{rupee(r.paid)}</TableCell>
                <TableCell className="text-right text-green-400">{rupee(r.saved)}</TableCell>
              </TableRow>
            ))}
            {!detail.length && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">No packages in this period.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
